import React, { FC, FocusEventHandler, ChangeEvent } from 'react';
import { time } from 'uniqid';
import { Checkbox } from './Checkbox';
import { Error } from '../Error';
import { includes, without } from 'lodash';
import { RADIO_BUTTON_VERTICAL_WIDTH } from '../constants';
import { useMedia } from '../util';

export type CheckboxGroupProps = {
  children?: React.ReactNode;
  id?: string;
  label?: string;
  direction?: 'col' | 'row';
  disabled?: boolean;
  selectedValues: string[];
  onChange: (selectedValues: string[]) => void;
  onBlur?: FocusEventHandler;
  onFocus?: FocusEventHandler;
  className?: string;
  name: string;
  options: {
    label: string;
    value: string;
    disabled?: boolean;
  }[];
  verticalWidth?: number;
  error?: string;
  showError?: boolean;
  showLabel?: boolean;
};
export const CheckboxGroup: FC<CheckboxGroupProps> = ({
  id = time('checkboxgroup'),
  direction = 'col',
  disabled = false,
  selectedValues,
  onChange,
  onBlur = () => true,
  onFocus = () => true,
  className = '',
  options,
  verticalWidth = RADIO_BUTTON_VERTICAL_WIDTH,
  children,
  name,
  showLabel = true,
  error,
  showError = true
}) => {
  const isVertical = useMedia([`(max-width: ${verticalWidth}px)`], [true], false);
  const checkboxDirection = isVertical || direction === 'col' ? 'checkbox-group-col' : 'checkbox-group-row';

  const makeHandleCheckboxChange = (value: string) => (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      onChange([...selectedValues, value]);
    } else {
      onChange(without(selectedValues, value));
    }
  };

  return (
    <fieldset id={id} data-testid={id} className={`checkbox-group ${className}`}>
      {children && (
        <legend id={`${id}-label`} className={`${!showLabel ? 'sr-only' : ''} ${disabled ? 'disabled' : ''}`}>
          {children}
        </legend>
      )}
      <div role="group" className={`d-flex ${checkboxDirection}`}>
        {options?.map((opt, index) => (
          <Checkbox
            id={`${id}-checkbox-${index}`}
            key={`${id}-checkbox-${index}`}
            name={name}
            tabIndex={disabled ? -1 : index+1}
            disabled={!!opt.disabled || disabled}
            checked={includes(selectedValues, opt.value)}
            onChange={makeHandleCheckboxChange(opt.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            ariaLabel={opt.label}>
            {opt.label}
          </Checkbox>
        ))}
      </div>
      <Error errors={error} showError={showError} />
    </fieldset>
  );
};

CheckboxGroup.displayName = 'CheckboxGroup';
export default CheckboxGroup;
